<template>
    <v-container class="pa-5" fluid>
        <page-title>
            FV z XL
        </page-title>

        <v-overlay :value="isRefreshing" opacity="0" absolute />

        <v-row justify="end">
            <v-col cols="12" sm="8" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="auto">
                            <v-menu
                                v-model="filtersMenu"
                                top
                                left
                                transition="scroll-x-reverse-transition"
                                :close-on-content-click="false"
                            >
                                <template #activator="{on}">
                                    <v-btn
                                        fab
                                        :color="hasFilters ? 'secondary' : 'grey darken-1'"
                                        elevation="1"
                                        small
                                        :disabled="isRefreshing"
                                        v-on="on"
                                    >
                                        <v-icon v-if="hasFilters" color="white">
                                            mdi-filter-plus
                                        </v-icon>
                                        <v-icon v-else color="white">
                                            mdi-filter-minus
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <v-card>
                                    <v-container fluid>
                                        <v-row justify="space-between" dense>
                                            <v-btn
                                                text
                                                color="primary"
                                                :disabled="!hasFilters || isRefreshing"
                                                @click="clearFilters"
                                            >
                                                Wyczyść filtry
                                            </v-btn>
                                            <v-col cols="auto">
                                                <v-btn icon small :disabled="isRefreshing" @click="toggleFiltersMenu()">
                                                    <v-icon color="grey darken-2">
                                                        mdi-close
                                                    </v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>

                                        <v-row justify="end" dense>
                                            <v-col cols="12" md="4">
                                                <v-text-field
                                                    v-model="filters.number"
                                                    append-icon="mdi-magnify"
                                                    label="Numer zlecenia"
                                                    single-line
                                                    clearable
                                                    solo
                                                    hide-details
                                                    :disabled="isRefreshing"
                                                    @click:append="changeFilter"
                                                    @click:clear="clearFilter('number')"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-text-field
                                                    v-model="filters.customerName"
                                                    append-icon="mdi-magnify"
                                                    label="Klient"
                                                    single-line
                                                    clearable
                                                    solo
                                                    hide-details
                                                    :disabled="isRefreshing"
                                                    @click:append="changeFilter"
                                                    @click:clear="clearFilter('customerName')"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-text-field
                                                    v-model="filters.invoiceNumber"
                                                    append-icon="mdi-magnify"
                                                    label="Numer FV"
                                                    single-line
                                                    clearable
                                                    solo
                                                    hide-details
                                                    :disabled="isRefreshing"
                                                    @click:append="changeFilter"
                                                    @click:clear="clearFilter('invoiceNumber')"
                                                />
                                            </v-col>
                                        </v-row>

                                        <v-row justify="end" dense>
                                            <v-col cols="12" md="4">
                                                <v-menu
                                                    v-model="createdAtMenuFrom"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.createdAt__gte"
                                                            label="Data utworzenia od"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            :disabled="isRefreshing"
                                                            v-on="on"
                                                            @click:clear="clearFilter('createdAt__gte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.createdAt__gte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="createdAtMenuFrom = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-menu
                                                    v-model="createdAtMenuTo"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.createdAt__lte"
                                                            label="Data utworzenia do"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            :disabled="isRefreshing"
                                                            v-on="on"
                                                            @click:clear="clearFilter('createdAt__lte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.createdAt__lte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="createdAtMenuTo = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card>
                            </v-menu>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="items"
                        :loading="loading"
                        :footer-props="footerProps"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :server-items-length="pagination.itemsLength"
                        :mobile-breakpoint="0"
                        name="fvFromXLData"
                        @pagination="updatePagination"
                    >
                        <template #item.customerName="{item}">
                            <template v-if="item.invoices && item.invoices.length">
                                <div class="text-center">
                                    <div v-for="invoice in item.invoices" :key="invoice.id">
                                        {{ parseEmptyValue(invoice.customerName) }}
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="text-center">
                                    {{ parseEmptyValue('') }}
                                </div>
                            </template>
                        </template>

                        <template #item.createdAt="{item}">
                            <template v-if="item.invoices && item.invoices.length">
                                <div class="text-center">
                                    <div v-for="invoice in item.invoices" :key="invoice.id">
                                        {{ formatDate(invoice.createdAt, false) }}
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="text-center">
                                    {{ parseEmptyValue('') }}
                                </div>
                            </template>
                        </template>

                        <template #item.invoiceNumber="{item}">
                            <template v-if="item.invoices && item.invoices.length">
                                <div class="text-center">
                                    <div v-for="invoice in item.invoices" :key="invoice.id">
                                        {{ parseEmptyValue(invoice.invoiceNumber) }}
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="text-center">
                                    {{ parseEmptyValue('') }}
                                </div>
                            </template>
                        </template>

                        <template #item.netSalesValue="{item}">
                            <template v-if="item.invoices && item.invoices.length">
                                <div class="text-right">
                                    <div v-for="invoice in item.invoices" :key="invoice.id">
                                        {{ formatNetSalesValue(invoice.netSalesValue) }}
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="text-right">
                                    {{ parseEmptyValue('') }}
                                </div>
                            </template>
                        </template>

                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Odśwież"
                                            :disabled="isRefreshing"
                                            v-on="on"
                                            @click="refreshItem(item)"
                                        >
                                            <v-progress-circular
                                                v-if="refreshLoadingRowId === item.id"
                                                indeterminate
                                                color="primary"
                                                size="18"
                                                width="2"
                                            />
                                            <v-icon v-else color="primary">
                                                mdi-autorenew
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Odśwież</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import { appConfig } from '@/config'
import tooltipDelay from '@/mixins/tooltip'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import parse from '@/mixins/parse'
import dateFormatter from '@/mixins/dateFormatter'

export default {
    name: 'ReportingAndAnalysisFVFromXL',
    components: {
        PageTitle,
    },
    mixins: [
        tooltipDelay,
        filters,
        pagination,
        parse,
        dateFormatter,
    ],
    data() {
        return {
            items: [],
            loading: false,
            isRefreshing: false,
            refreshLoadingRowId: null,
            filtersMenu: false,
            hasFilters: false,
            createdAtMenuFrom: false,
            createdAtMenuTo: false,
            headers: [
                {
                    text: 'Numer zlecenia',
                    value: 'number',
                    sortable: false,
                },
                {
                    text: 'Klient',
                    value: 'customerName',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdAt',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Numer FV',
                    value: 'invoiceNumber',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Wartość netto sprzedaży',
                    value: 'netSalesValue',
                    sortable: false,
                    align: 'end',
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 140,
                },
            ],
        }
    },
    computed: {
        footerProps() {
            return {
                itemsPerPageOptions: appConfig.common.rowsPerPageItems,
            }
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${encodeURIComponent(filters[item])}`)
                }
            }

            return query
        }
    },
    mounted() {
        this.resetPagination()
        this.checkFilters()
        this.getItems()
    },
    methods: {
        toggleFiltersMenu() {
            if (this.isRefreshing) {
                return
            }
            this.filtersMenu = !this.filtersMenu
        },
        checkFilters() {
            this.hasFilters = !Object.values(this.filters).every(item => item == null || item.length == 0)
        },
        clearFilters() {
            if (this.isRefreshing) {
                return
            }
            Object.keys(this.filters).forEach(key => {
                this.filters[key] = null
            })
            this.checkFilters()
            this.changeFilter()
        },
        changeFilter() {
            if (this.isRefreshing) {
                return
            }
            this.resetPagination()
            this.checkFilters()
            this.getItems()
        },
        getItems() {
            this.loading = true

            return this.$store
                .dispatch('reportsAndAnalysis/fvFromXL/getFVFromXLInvoices', {
                    paginate: this.paginate(),
                    query: this.filterQuery,
                })
                .then(response => {
                    this.handlePaginationResponse(response)
                    this.items = response.data.results

                    return Promise.resolve(response)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        updatePagination(pagination) {
            if (this.isRefreshing) {
                return
            }
            this.pagination = pagination

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getItems()
            }
            this.clearPaginationPending()
        },
        formatNetSalesValue(value) {
            if (value == null || value === '') {
                return this.parseEmptyValue(value)
            }


            const numberValue = Number(value)

            if (Number.isNaN(numberValue)) {
                return this.parseEmptyValue('')
            }

            return numberValue.toFixed(2)
        },
        refreshItem(item) {
            if (this.isRefreshing) {
                return
            }

            this.isRefreshing = true
            this.refreshLoadingRowId = item.id

            return this.$store
                .dispatch('reportsAndAnalysis/fvFromXL/refreshFVFromXLInvoicesForJob', { id: item.id })
                .then(response => {
                    return this.getItems().then(() => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Lista faktur dla pozycji została odświeżona',
                        })

                        return Promise.resolve(response)
                    })
                })
                .catch(error => {
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: 'Nie udało się odświeżyć listy faktur dla pozycji',
                    })

                    return Promise.reject(error)
                })
                .finally(() => {
                    this.isRefreshing = false
                    this.refreshLoadingRowId = null
                })
        },
    },
}
</script>
